import { useEffect, useRef, useState, type RefObject } from 'react';
import { get, post, qs } from '../lib/api';
import { navigate, useDebounced, useLoad } from '../lib/hooks';
import { useSession } from '../lib/session';
import { formatDateTime, formatPhone, formatTime } from '../../shared/format';
import { smsParts } from '../../shared/messaging';
import { Badge, Button, Confirm, Empty, Field, Sheet, Spinner, Tabs, useAction, useToast } from '../ui/kit';
import { Icon } from '../ui/icons';
import { CustomerPicker } from './CustomerPicker';

// Messages & Calls: every text with a customer is one conversation. Staff answering takes it over
// from the after-hours assistant; missed calls get a text and show up here to call back.

interface ThreadSummary {
  id: string;
  phone: string;
  customer_id: string | null;
  name: string;
  mode: 'auto' | 'staff';
  taken_by_name: string | null;
  needs_staff: boolean;
  needs_staff_note: string | null;
  unread: number;
  last_message_at: number | null;
  last_preview: string | null;
  opted_out: boolean;
}

interface Msg {
  id: string;
  direction: 'in' | 'out';
  author: 'staff' | 'ai' | 'auto' | 'system' | 'customer' | null;
  body: string;
  status: string;
  error: string | null;
  template_key: string | null;
  media: string[];
  created_at: number;
  employee_name: string | null;
}

interface Thread extends ThreadSummary {
  customer: { id: string; name: string; sms_opt_in: boolean; consent_at: number | null; consent_source: string | null } | null;
  can_text: boolean;
  connected: boolean;
  /** The owner's one-tap replies (Settings → Messages & calls). */
  quick_replies?: string[];
  messages: Msg[];
  repairs: { id: string; ticket: string; device: string; status_label: string }[];
  calls: { id: string; status: string; missed: number; texted_back: number; after_hours: number; created_at: number }[];
}

type Filter = 'all' | 'unread' | 'needs_staff';

/** How long ago, short: "now", "5m", "3h", "Tue", "Oct 2". */
function ago(ms: number | null): string {
  if (!ms) return '';
  const d = Date.now() - ms;
  if (d < 60_000) return 'now';
  if (d < 3_600_000) return `${Math.floor(d / 60_000)}m`;
  if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}h`;
  if (d < 6 * 86_400_000) return new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(new Date(ms));
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(ms));
}

/** Polls while the tab is visible. */
function useEvery(fn: () => void, ms: number) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === 'visible') ref.current(); }, ms);
    return () => clearInterval(t);
  }, [ms]);
}

/**
 * A quick reply as it goes in the box: {first} is the customer's first name and {store} the
 * store's name, with plain quotes (a curly one makes a text cost twice as much).
 */
function fillQuickReply(text: string, first: string | null, store: string): string {
  return text
    .replace(/\{first\}/g, first || 'there')
    .replace(/\{store\}/g, store)
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"');
}

/** One-tap replies: each goes into the reply box for staff to edit and send. Nothing is sent by itself. */
function QuickReplies({ replies, onPick }: { replies: string[] | undefined; onPick: (text: string) => void }) {
  if (!replies?.length) return null;
  return (
    <div className="quick-replies" role="group" aria-label="Quick replies">
      <span className="tiny muted">Quick replies</span>
      <div className="chips">
        {replies.map((q) => (
          <button key={q} type="button" className="chip" title={q} onClick={() => onPick(q)}><span>{q}</span></button>
        ))}
      </div>
    </div>
  );
}

/** Puts a quick reply in the box (after what's typed), then the cursor at the end, ready to edit. */
function quickReplyPicker(setBody: (fn: (b: string) => string) => void, box: RefObject<HTMLTextAreaElement | null>, first: string | null, store: string) {
  return (q: string) => {
    const text = fillQuickReply(q, first, store);
    setBody((b) => (b.trim() ? `${b.trimEnd()} ${text}` : text));
    requestAnimationFrame(() => {
      const el = box.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
  };
}

export function MessagesScreen({ id, query }: { id?: string; query: URLSearchParams }) {
  const tab = id === 'calls' ? 'calls' : 'texts';
  const threadId = tab === 'texts' ? id : undefined;
  return (
    <div className="page">
      <div className="page-head">
        <h1>Messages</h1>
        <Tabs label="Messages" value={tab} onChange={(t) => navigate(t === 'calls' ? '/messages/calls' : '/messages')} tabs={[{ id: 'texts', label: 'Texts' }, { id: 'calls', label: 'Calls' }]} />
      </div>
      {tab === 'calls' ? <CallsTab /> : <TextsTab threadId={threadId} customerId={query.get('customer')} />}
    </div>
  );
}

function TextsTab({ threadId, customerId }: { threadId?: string; customerId: string | null }) {
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  const list = useLoad(() => get<ThreadSummary[]>(`/api/messages/threads${qs({ filter: filter === 'all' ? '' : filter, q: dq })}`), [filter, dq]);
  const [composing, setComposing] = useState<boolean>(!!customerId);
  const [broadcasting, setBroadcasting] = useState(false);
  const { can } = useSession();
  useEvery(list.reload, 10_000);

  return (
    <div className={`msg-layout ${threadId ? 'has-thread' : ''}`}>
      <section className="msg-list" aria-label="Conversations">
        <div className="row-wrap">
          <div className="search grow">
            <Icon.search />
            <input className="input" placeholder="Name, number or words" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search texts" />
          </div>
          <Button variant="primary" icon="plus" onClick={() => setComposing(true)}>New text</Button>
          {can('employees.manage') ? <Button icon="users" onClick={() => setBroadcasting(true)}>Text all customers</Button> : null}
        </div>
        <div className="chips" role="group" aria-label="Show">
          {([['all', 'All'], ['unread', 'Unread'], ['needs_staff', 'Needs a reply']] as [Filter, string][]).map(([k, label]) => (
            <button key={k} type="button" className="chip" aria-pressed={filter === k} onClick={() => setFilter(k)}>{label}</button>
          ))}
        </div>
        {list.data ? (
          list.data.length ? (
            <div className="list">
              {list.data.map((t) => (
                <a key={t.id} className={`list-row msg-row ${t.unread ? 'unread' : ''}`} href={`#/messages/${t.id}`} aria-current={t.id === threadId ? 'true' : undefined}>
                  <span className="avatar avatar-sm" aria-hidden>{t.customer_id ? t.name.slice(0, 1).toUpperCase() : '#'}</span>
                  <span className="main-col">
                    <span className="spread"><span className="t">{t.name}</span><span className="tiny muted nowrap">{ago(t.last_message_at)}</span></span>
                    <span className="s" style={{ display: 'block' }}>{t.last_preview || ' '}</span>
                    <span className="row-wrap" style={{ marginTop: 4 }}>
                      {t.unread ? <Badge tone="blue">{t.unread} new</Badge> : null}
                      {t.needs_staff ? <Badge tone="amber" icon="alert">{t.needs_staff_note || 'Needs a reply'}</Badge> : null}
                      {t.mode === 'staff' ? <Badge icon="user">{t.taken_by_name ? `${t.taken_by_name.split(' ')[0]} has it` : 'Staff'}</Badge> : null}
                      {t.opted_out ? <Badge tone="red">Replied STOP</Badge> : null}
                    </span>
                  </span>
                </a>
              ))}
            </div>
          ) : (
            <Empty icon="chat" title={filter === 'all' && !dq ? 'No texts yet.' : 'Nothing here.'}>
              {filter === 'all' && !dq ? 'When customers text the store’s number, or a ready text goes out, the conversation shows here.' : null}
            </Empty>
          )
        ) : <Spinner />}
      </section>
      <section className="msg-thread" aria-label="Conversation">
        {threadId ? <ThreadView key={threadId} id={threadId} onChanged={list.reload} /> : (
          <div className="msg-empty"><Empty icon="chat" title="Pick a conversation">Answering a text takes it over from the assistant. It goes back to automatic replies later, or when you tap “Let the assistant answer”.</Empty></div>
        )}
      </section>
      {broadcasting ? <BroadcastSheet onClose={() => setBroadcasting(false)} /> : null}
      {composing ? <NewTextSheet customerId={customerId} onClose={() => setComposing(false)} onSent={(tid) => { setComposing(false); list.reload(); navigate(`/messages/${tid}`); }} /> : null}
    </div>
  );
}

const AUTHOR: Record<string, string> = { staff: '', ai: 'Assistant', auto: 'Automatic', system: 'STORE OS' };
const STATUS: Record<string, string> = {
  queued: 'Sending…', sending: 'Sending…', sent: 'Sent', delivered: 'Delivered', failed: 'Not delivered', undelivered: 'Not delivered',
  not_configured: 'Saved (texting not connected)', blocked: 'Not sent', received: '',
};

function ThreadView({ id, onChanged }: { id: string; onChanged: () => void }) {
  const toast = useToast();
  const { status } = useSession();
  const t = useLoad(() => get<Thread>(`/api/messages/threads/${id}`), [id]);
  const { run, busy } = useAction();
  const [body, setBody] = useState('');
  const [stopping, setStopping] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const first = t.data?.customer ? t.data.customer.name.split(' ')[0] : null;
  const pickQuick = quickReplyPicker(setBody, box, /\d/.test(first ?? '') ? null : first, status?.business_name || 'West Coast Wireless');
  useEvery(t.reload, 5_000);
  const count = t.data?.messages.length ?? 0;
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [count]);
  // Opening a conversation marks it read; the list catches up.
  useEffect(() => { if (t.data) onChanged(); }, [t.data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!t.data) return t.error ? <Empty icon="alert" title="Couldn’t open this conversation.">{t.error.message}</Empty> : <Spinner />;
  const th = t.data;

  const act = async (path: string, payload?: unknown, success?: string) => {
    const r = await run(() => post(`/api/messages/threads/${id}/${path}`, payload), { success });
    if (r) { t.setData(r.thread ?? r); onChanged(); }
    return r;
  };
  const send = async () => {
    const text = body.trim();
    if (!text) return;
    const r = await act('reply', { body: text });
    if (!r) return;
    if (r.result.status === 'blocked') toast(r.result.message, 'error');
    else setBody('');
  };
  const parts = smsParts(body);

  return (
    <div className="msg-thread-inner">
      <div className="msg-head">
        <a className="icon-btn msg-back" href="#/messages" aria-label="Back to conversations"><Icon.chevronLeft /></a>
        <div className="grow">
          <div className="strong">{th.customer ? <a href={`#/customers/${th.customer.id}`}>{th.name}</a> : th.name}</div>
          <div className="small muted">{formatPhone(th.phone)}{th.customer ? (th.customer.sms_opt_in ? ' · agreed to texts' : ' · hasn’t agreed to texts') : ' · not a customer yet'}</div>
        </div>
        <a className="btn btn-secondary btn-sm" href={`tel:${th.phone}`}><Icon.phone />Call</a>
        {th.mode === 'staff' ? (
          <Button size="sm" icon="spark" busy={busy} onClick={() => act('mode', { mode: 'auto' }, 'The assistant can answer again.')}>Let the assistant answer</Button>
        ) : (
          <Button size="sm" icon="user" busy={busy} onClick={() => act('mode', { mode: 'staff' }, 'You have this conversation.')}>Take over</Button>
        )}
        {th.needs_staff ? <Button size="sm" icon="check" busy={busy} onClick={() => act('done', undefined, 'Marked done.')}>Done</Button> : null}
      </div>

      {/* Notices and the customer's tickets: a short block that scrolls, so the conversation and the compose box keep the room on a short screen. */}
      {th.needs_staff || th.mode === 'staff' || !th.connected || th.repairs.length ? (
        <div className="msg-notes">
          {th.needs_staff ? <div className="banner warn"><Icon.alert /><div><strong>Needs a reply:</strong> {th.needs_staff_note || 'the customer is waiting for a person.'}</div></div> : null}
          {th.mode === 'staff' ? <div className="banner"><Icon.user /><div className="small">{th.taken_by_name ?? 'Staff'} took over. No automatic replies until someone lets the assistant answer again (or after a while with no answer from staff).</div></div> : null}
          {!th.connected ? <div className="banner warn"><Icon.info /><div className="small">Texting isn’t connected yet. Texts are saved and go out once Twilio is set up in Settings → Messages & calls.</div></div> : null}
          {th.repairs.length ? (
            <div className="msg-tickets">
              {th.repairs.map((r) => <a key={r.id} className="chip" href={`#/repairs/${r.id}`}><Icon.wrench />{r.ticket} · {r.device} · {r.status_label}</a>)}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="msg-bubbles" aria-live="polite">
        {th.messages.map((m, i) => {
          const prev = th.messages[i - 1];
          const day = !prev || new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString();
          const who = m.direction === 'in' ? '' : m.author === 'staff' ? m.employee_name ?? 'Staff' : AUTHOR[m.author ?? 'system'] ?? '';
          const bad = ['failed', 'undelivered', 'blocked'].includes(m.status);
          return (
            <div key={m.id}>
              {day ? <div className="msg-day">{formatDateTime(m.created_at).replace(/,? \d{1,2}:\d{2}.*$/, '')}</div> : null}
              <div className={`bubble ${m.direction === 'in' ? 'in' : 'out'} ${m.author === 'ai' ? 'ai' : ''} ${m.author === 'system' || m.author === 'auto' ? 'sys' : ''} ${bad ? 'bad' : ''}`}>
                <div className="bubble-body">{m.body}</div>
                {m.media.map((u) => <div key={u} className="tiny"><a href={u} target="_blank" rel="noreferrer">Picture they sent</a></div>)}
                <div className="bubble-meta">
                  {m.author === 'ai' ? <Icon.spark /> : null}
                  {[who, formatTime(m.created_at), m.direction === 'out' ? STATUS[m.status] ?? m.status : ''].filter(Boolean).join(' · ')}
                  {bad && m.error ? ` (${m.error === 'opted_out' ? 'replied STOP' : m.error === 'no_consent' ? 'no texting agreement' : m.error})` : ''}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={end} />
      </div>

      {th.opted_out ? (
        <div className="banner warn"><Icon.alert /><div className="small">This number replied STOP. Nothing can be texted to it until they text START to the store’s number.</div></div>
      ) : (
        <div className="msg-compose">
          {!th.can_text ? <div className="small muted">They haven’t agreed to texts and haven’t texted or called us lately, so a text won’t go out. Tick the texting box at their next checkout, or ask them to text the store first.</div> : null}
          <QuickReplies replies={th.quick_replies} onPick={pickQuick} />
          <textarea
            ref={box}
            className="textarea"
            rows={3}
            placeholder={th.customer ? `Text ${th.name.split(' ')[0]}…` : 'Text them…'}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }}
            aria-label="Message"
            maxLength={1000}
          />
          <div className="spread">
            <span className="tiny muted">{body ? `${body.length} characters · ${parts} text${parts === 1 ? '' : 's'}` : 'Sending takes over from the assistant.'}</span>
            <span className="row-wrap">
              <Button size="sm" variant="ghost" onClick={() => setStopping(true)}>Stop texts</Button>
              <Button variant="primary" icon="send" busy={busy} disabled={!body.trim()} onClick={send}>Send</Button>
            </span>
          </div>
        </div>
      )}
      {stopping ? (
        <Confirm title="Stop texts to this number?" confirmLabel="Stop texts" danger busy={busy} onClose={() => setStopping(false)} onConfirm={async () => { await act('stop', undefined, 'Texts stopped.'); setStopping(false); }}>
          Use this when the customer asks in person not to get texts. Only they can turn texts back on, by texting START to the store’s number.
        </Confirm>
      ) : null}
    </div>
  );
}

function NewTextSheet({ customerId, onClose, onSent }: { customerId: string | null; onClose: () => void; onSent: (threadId: string) => void }) {
  const toast = useToast();
  const { status } = useSession();
  const { run, busy } = useAction();
  const [to, setTo] = useState<{ id: string | null; name: string; phone: string | null } | null>(null);
  const [picking, setPicking] = useState(false);
  const [phone, setPhone] = useState('');
  const [body, setBody] = useState('');
  const box = useRef<HTMLTextAreaElement>(null);
  const quick = useLoad(() => get<{ quick_replies?: string[] }>('/api/messages/status'), []);
  const first = to?.id ? to.name.split(' ')[0] : null;
  const pickQuick = quickReplyPicker(setBody, box, /\d/.test(first ?? '') ? null : first, status?.business_name || 'West Coast Wireless');
  useEffect(() => {
    if (!customerId) return;
    get(`/api/customers/${customerId}`).then((c) => setTo({ id: c.id, name: c.name, phone: c.phone })).catch(() => undefined);
  }, [customerId]);
  const send = async () => {
    const r = await run(() => post('/api/messages/threads', { customerId: to?.id ?? null, phone: to ? null : phone, body }));
    if (!r) return;
    if (r.result.status === 'blocked') toast(r.result.message, 'error');
    else toast(r.result.message, 'good');
    onSent(r.thread.id);
  };
  if (picking) return <CustomerPicker onClose={() => setPicking(false)} onPick={(c) => { setTo({ id: c.id, name: c.name, phone: c.phone }); setPicking(false); }} />;
  return (
    <Sheet
      title="New text"
      onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" icon="send" busy={busy} disabled={!body.trim() || (!to && phone.replace(/\D/g, '').length < 10)} onClick={send}>Send</Button></>}
    >
      <div className="stack">
        {to ? (
          <div className="customer-pill">
            <span className="avatar avatar-sm" aria-hidden>{to.name.slice(0, 1)}</span>
            <div className="grow"><div className="strong">{to.name}</div><div className="small muted">{formatPhone(to.phone) || 'No phone'}</div></div>
            <Button size="sm" variant="ghost" onClick={() => setTo(null)}>Change</Button>
          </div>
        ) : (
          <div className="row-wrap">
            <Button icon="user" onClick={() => setPicking(true)}>Pick a customer</Button>
            <span className="muted small">or</span>
            <Field label="Phone number" className="grow"><input className="input" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
          </div>
        )}
        <QuickReplies replies={quick.data?.quick_replies} onPick={pickQuick} />
        <Field label="Message"><textarea ref={box} className="textarea" rows={4} value={body} onChange={(e) => setBody(e.target.value)} maxLength={1000} /></Field>
        <p className="small muted">Texts only go to customers who agreed to texts (the box at checkout) or texted us in the last 30 days (called in the last 7), and never to a number that replied STOP.</p>
      </div>
    </Sheet>
  );
}

function callOutcome(c: any): string {
  const mins = (sec: number | null) => (sec ? ` (${Math.max(1, Math.round(sec / 60))} min)` : '');
  if (c.handled_by === 'agent') return c.transferred ? (c.status === 'transferred' ? 'Phone agent, then staff' : 'Phone agent · transfer not answered') : `Phone agent${mins(c.duration_s)}`;
  if (c.status === 'answered') return `Answered${mins(c.duration_s)}`;
  if (c.status === 'ringing') return 'Ringing';
  return c.after_hours ? 'After hours' : 'Missed';
}

function CallsTab() {
  const calls = useLoad(() => get<any[]>('/api/messages/calls?limit=100'), []);
  const [open, setOpen] = useState<string | null>(null);
  useEvery(calls.reload, 15_000);
  if (!calls.data) return <Spinner />;
  if (!calls.data.length) return <Empty icon="phone" title="No calls yet.">Calls to the store’s Twilio number show here, with the recording and what was said.</Empty>;
  return (
    <>
      <div className="list">
        {calls.data.map((c) => {
          const missed = !!c.missed && c.handled_by !== 'agent';
          return (
            <div key={c.id} className="list-row call-row" role="button" tabIndex={0} onClick={() => setOpen(c.id)} onKeyDown={(e) => { if (e.key === 'Enter') setOpen(c.id); }}>
              {c.handled_by === 'agent' ? <Icon.spark /> : missed ? <Icon.phoneMissed /> : <Icon.phone />}
              <span className="main-col">
                <span className="t" style={{ display: 'block' }}>{c.name}</span>
                <span className="s" style={{ display: 'block' }}>
                  {formatDateTime(c.created_at)} · {callOutcome(c)}{c.from_phone ? ` · ${formatPhone(c.from_phone)}` : ''}
                </span>
                {c.summary ? <span className="call-summary">{c.summary}</span> : null}
                <span className="row-wrap" style={{ marginTop: 4 }}>
                  {c.needs_reply ? <Badge tone="amber" icon="alert">{c.reply_note || 'Needs a reply'}</Badge> : null}
                  {missed ? (c.texted_back ? <Badge tone="green" icon="chat">Texted back</Badge> : <Badge tone="amber">Not texted</Badge>) : null}
                  {c.has_recording ? <Badge icon="phone">Recorded</Badge> : null}
                </span>
              </span>
              {c.from_phone ? <a className="btn btn-secondary btn-sm" href={`tel:${c.from_phone}`} onClick={(e) => e.stopPropagation()}><Icon.phone />Call back</a> : null}
            </div>
          );
        })}
      </div>
      {open ? <CallSheet id={open} onClose={() => setOpen(null)} onChanged={calls.reload} /> : null}
    </>
  );
}

/** One call: summary, recording, everything that was said, and the follow-up. */
function CallSheet({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const c = useLoad(() => get<any>(`/api/messages/calls/${id}`), [id]);
  const { run, busy } = useAction();
  const k = c.data;
  return (
    <Sheet
      title={k ? k.name : 'Call'}
      onClose={onClose}
      wide
      footer={k ? (
        <>
          {k.customer_id ? <a className="btn btn-secondary" href={`#/customers/${k.customer_id}`}><Icon.user />Customer</a> : null}
          {k.thread_id ? <a className="btn btn-secondary" href={`#/messages/${k.thread_id}`}><Icon.chat />Texts</a> : null}
          {k.from_phone ? <a className="btn btn-secondary" href={`tel:${k.from_phone}`}><Icon.phone />Call back</a> : null}
          {k.needs_reply ? <Button variant="primary" icon="check" busy={busy} onClick={async () => { const r = await run(() => post(`/api/messages/calls/${id}/done`), { success: 'Marked as called back.' }); if (r) { c.setData(r); onChanged(); } }}>Called back</Button> : null}
        </>
      ) : null}
    >
      {!k ? <Spinner /> : (
        <div className="stack">
          <div className="small muted">{formatDateTime(k.created_at)} · {callOutcome(k)}{k.from_phone ? ` · ${formatPhone(k.from_phone)}` : ''}</div>
          {k.needs_reply ? <div className="banner warn"><Icon.alert /><div><strong>Needs a reply:</strong> {k.reply_note || 'call them back.'}</div></div> : null}
          {k.summary ? <div className="card card-tight"><div className="small strong">Summary</div><div>{k.summary}</div></div> : null}
          {k.has_recording ? (
            <div className="stack-sm">
              <div className="small strong">Recording{k.recording_duration_s ? ` · ${Math.floor(k.recording_duration_s / 60)}:${String(k.recording_duration_s % 60).padStart(2, '0')}` : ''}</div>
              <audio controls preload="none" src={`/api/messages/calls/${id}/recording`} style={{ width: '100%' }} />
            </div>
          ) : null}
          {k.transcript.length ? (
            <div className="stack-sm">
              <div className="small strong">What was said</div>
              <div className="msg-bubbles call-transcript">
                {k.transcript.map((l: any, i: number) => (
                  <div key={i} className={`bubble ${l.role === 'caller' ? 'in' : 'out ai'}`}>
                    <div className="bubble-body">{l.text}</div>
                    <div className="bubble-meta">{l.role === 'caller' ? 'Caller' : <><Icon.spark />Phone agent</>} · {formatTime(l.at)}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : k.handled_by === 'agent' ? null : <p className="small muted">{k.has_recording ? 'Staff answered this call; listen to the recording above.' : 'Nothing was recorded on this call.'}</p>}
        </div>
      )}
    </Sheet>
  );
}

/** Unread conversations, for the badge on the menu. */
export function useMessagesBadge(enabled: boolean): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    const load = () => get<{ unread_threads: number; needs_staff: number }>('/api/messages/status')
      .then((s) => { if (live) setN(Math.max(s.unread_threads, s.needs_staff)); })
      .catch(() => undefined);
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 20_000);
    window.addEventListener('hashchange', load);
    return () => { live = false; clearInterval(t); window.removeEventListener('hashchange', load); };
  }, [enabled]);
  return n;
}

/** Can staff use texting at all here (permission)? */
export function useCanMessage(): boolean {
  const { can } = useSession();
  return can('messages.use');
}

/** The text as customers get it: the store's name first, how to stop them last (as the server sends it). */
function broadcastPreview(store: string, body: string): string {
  let t = body.replace(/\s+\n/g, '\n').trim();
  if (!t) return '';
  if (!t.toLowerCase().startsWith(store.toLowerCase())) t = `${store}: ${t}`;
  if (!/\bstop\b/i.test(t)) t = `${t} Reply STOP to opt out.`;
  return t;
}

/**
 * "Text all customers": one text to many customers. Either the ones marked "wants deals", or (the
 * owner, confirming they all agreed to texts) every customer in STORE OS. Never anyone who replied STOP.
 */
function BroadcastSheet({ onClose }: { onClose: () => void }) {
  const { status } = useSession();
  const toast = useToast();
  const aud = useLoad<any>(() => get('/api/messages/broadcast'), []);
  const { run, busy } = useAction();
  const [body, setBody] = useState('');
  const [who, setWho] = useState<'all' | 'deals'>('all');
  const [agreed, setAgreed] = useState(false);
  const [sure, setSure] = useState(false);
  const store = status?.business_name || 'West Coast Wireless';
  const preview = broadcastPreview(store, body).replace(/\{first\}/g, 'Maria');
  const a = aud.data;
  const all = who === 'all' && !!a?.can_send_all;
  const count = a ? (all ? a.all_customers : a.can_text) : 0;
  const ready = !!body.trim() && count > 0 && preview.length <= (a?.max_chars ?? 480) && (!all || agreed);
  const send = async () => {
    const r = await run(() => post('/api/messages/broadcast', { body, audience: all ? 'all' : 'deals', confirmAll: all ? agreed : undefined }));
    setSure(false);
    if (r) {
      toast(r.connected ? `Sending to ${r.sent} customers.` : `Saved for ${r.sent} customers. They go out once texting is connected.`, 'good');
      onClose();
    }
  };
  return (
    <>
    <Sheet title="Text all customers" onClose={onClose} wide footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" icon="send" disabled={!ready} onClick={() => setSure(true)}>{a ? `Send to ${count} customer${count === 1 ? '' : 's'}` : 'Send'}</Button></>}>
      {aud.error ? <p className="muted">{aud.error.message}</p> : !a ? <Spinner /> : (
        <div className="stack-sm">
          <div className="chips" role="group" aria-label="Who gets it">
            {a.can_send_all ? <button type="button" className="chip" aria-pressed={all} onClick={() => setWho('all')}>All customers ({a.all_customers})</button> : null}
            <button type="button" className="chip" aria-pressed={!all} onClick={() => setWho('deals')}>Marked “wants deals” ({a.can_text})</button>
          </div>
          {all ? (
            <label className="check confirm-all">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
              <span>I confirm every customer in STORE OS agreed to get texts from {store}, including deals and news. This is saved with the send.</span>
            </label>
          ) : null}
          {a.stopped ? <p className="small muted">{a.stopped} customer{a.stopped === 1 ? '' : 's'} replied STOP and won’t get it.</p> : null}
          <Field label="Your text" hint="{first} becomes their first name">
            <textarea className="input" rows={4} value={body} onChange={(e) => setBody(e.target.value)} maxLength={420} placeholder="This weekend only: 20% off cases and screen protectors with any repair. See you soon!" autoFocus />
          </Field>
          {preview ? (
            <>
              <div className="small strong">How it looks</div>
              <div className="bubble out">{preview}</div>
              <div className={`small ${preview.length > a.max_chars ? 'overdue' : 'muted'}`}>{preview.length} characters · {smsParts(preview)} text{smsParts(preview) === 1 ? '' : 's'} each</div>
            </>
          ) : null}
          {!a.connected ? <p className="small muted">Texting isn’t connected yet: the texts are saved and go out once it is.</p> : null}
        </div>
      )}
    </Sheet>
    {sure && a ? (
      <Confirm title={`Text ${count} customers?`} confirmLabel={`Send to ${count}`} busy={busy} onConfirm={send} onClose={() => setSure(false)}>
        <p>{all ? 'Every customer in STORE OS' : 'Everyone marked “wants deals”'} gets this text now. It can’t be taken back.</p>
        <div className="bubble out">{preview}</div>
      </Confirm>
    ) : null}
    </>
  );
}
